import React, { useState, createContext, useContext } from 'react';
import { Layout, Menu, Dropdown, Avatar, Divider, Badge, theme } from 'antd';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined,
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  WechatOutlined,
  FileTextOutlined,
  UserAddOutlined,
  SendOutlined,
  RobotOutlined,
  TeamOutlined,
  HeartOutlined,
  EnvironmentOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import { useAuth } from '../utils/auth';
import { processAvatarUrl } from '../utils/urlUtils';

const { Header, Sider, Content } = Layout;
const { SubMenu } = Menu;
const { useToken } = theme;

// 创建侧边栏上下文
const SiderContext = createContext();

// 导出hook供其他组件使用
export const useSider = () => {
  const context = useContext(SiderContext);
  if (!context) {
    throw new Error('useSider must be used within SiderProvider');
  }
  return context;
};

const AppLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, tenant, logout, isSystemAdmin } = useAuth();
  const { token } = useToken(); // 使用 antd hook 获取主题 token

  // 侧边栏宽度配置
  const SIDER_WIDTH = 200;
  const SIDER_COLLAPSED_WIDTH = 96; // Antd默认收缩宽度

  // 获取当前侧边栏宽度
  const getCurrentSiderWidth = () => {
    return collapsed ? SIDER_COLLAPSED_WIDTH : SIDER_WIDTH;
  };

  const onMenuClick = ({ key }) => {
    navigate(key);
  };

  // 基于当前路径生成默认选中键
  const getDefaultSelectedKey = () => {
    return location.pathname;
  };

  // 用户菜单下拉项
  const userMenu = (
    <Menu>
      <Menu.Item key="logout" icon={<LogoutOutlined />} onClick={logout}>
        退出登录
      </Menu.Item>
    </Menu>
  );

  // Logo样式
  const logoStyle = {
    height: 'auto',
    padding: '16px',
    backgroundColor: token.colorWhite,
    borderBottom: '1px solid rgb(224, 224, 224)',
    color: '#4D4D4D',
    fontWeight: 'bold',
    fontSize: '18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: '32px',
  };

  return (
    <SiderContext.Provider value={{ 
      collapsed, 
      siderWidth: getCurrentSiderWidth(),
      expandedWidth: SIDER_WIDTH,
      collapsedWidth: SIDER_COLLAPSED_WIDTH
    }}>
      <Layout style={{ minHeight: '100vh' }}>
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={(value) => setCollapsed(value)}
          width={SIDER_WIDTH}
          collapsedWidth={SIDER_COLLAPSED_WIDTH}
          style={{ backgroundColor: token.siderBg }} // 使用 hook 获取的 token
        >
        <div style={logoStyle}>
          <img 
            src="/logo.jpg" 
            alt="爱推SCRM" 
            style={{ 
              width: '32px', 
              height: '32px', 
              marginRight: collapsed ? '-8px' : '8px',
              borderRadius: '4px',
              objectFit: 'cover'
            }} 
          />
          {!collapsed && '爱推SCRM'}
        </div>
        <Menu
          mode="inline"
          selectedKeys={[getDefaultSelectedKey()]}
          defaultOpenKeys={['group-send', 'ai-agent', 'content']}
          onClick={onMenuClick}
          style={{
            margin: '15px 15px',
            width: 'auto',
            borderRadius: '8px',
            marginRight: '0px',
          }}
        >
          {/* 首页 */}
          <Menu.Item key="/dashboard" icon={<DashboardOutlined />}>
            首页
          </Menu.Item>

          {/* 拉新 */}
          <SubMenu key="contact" icon={<UserAddOutlined />} title="拉新">
            <Menu.Item key="/wework-contact-way">
              成员活码
            </Menu.Item>
            <Menu.Item key="/wework-group-join-way">
              社群活码
            </Menu.Item>
            <Menu.Item key="/lbs-contact-ways">
              LBS活码
            </Menu.Item>
            <Menu.Item key="/wework-non-channel-welcome">
              成员欢迎语
            </Menu.Item>
            <Menu.Item key="/wework-group-welcome-templates">
              入群欢迎语
            </Menu.Item>
          </SubMenu>

          {/* 群发 */}
          <SubMenu key="group-send" icon={<SendOutlined />} title="群发" >
            <Menu.Item key="/customer-mass-send">
              客户群发
            </Menu.Item>
            <Menu.Item key="/group-mass-send">
              社群群发
            </Menu.Item>
            <Menu.Item key="/moment-mass-send">
              朋友圈群发
            </Menu.Item>
            <Menu.Item key="/scheduled-sop-templates">
              聚合群发
            </Menu.Item>
            <Menu.Item key="/monitor">
              群发监控
            </Menu.Item>
          </SubMenu>

          {/* 智能体 */}
          <SubMenu key="ai-agent" icon={<RobotOutlined />} title="智能体">
            <Menu.Item key="/sop-templates">
              智能体
            </Menu.Item>
            <Menu.Item key="/customer-journey">
              客户旅程
            </Menu.Item>
          </SubMenu>
          {/* 内容 */}
          <SubMenu key="content" icon={<FileTextOutlined />} title="内容">
            <Menu.Item key="/ai-content">
              AI生成
            </Menu.Item>
            <Menu.Item key="/materials">
              素材管理
            </Menu.Item>
          </SubMenu>
          {/* 客户 */}
          <SubMenu key="customers" icon={<UserOutlined />} title="客户">
            <Menu.Item key="/customers">
              企微好友
            </Menu.Item>
            <Menu.Item key="/groups">
              企微社群
            </Menu.Item>
            <Menu.Item key="/tags">
              标签列表
            </Menu.Item>
          </SubMenu>

          {/* 数据 */}
          <SubMenu key="analytics" icon={<BarChartOutlined />} title="数据">
            <Menu.Item key="/customer-relationship-analysis">
              企微好友分析
            </Menu.Item>
            <Menu.Item key="/group-relationship-analysis">
              企微社群分析
            </Menu.Item>
          </SubMenu>

          <Divider style={{ margin: '8px 120px 8px 8px', borderColor: '#F1F1F1' }} />

          {/* 设置 */}
          <SubMenu key="wework" icon={<SettingOutlined />} title="设置">
            <Menu.Item key="/wework-employees">
              成员列表
            </Menu.Item>
            <Menu.Item key="/wework-employee-groups">
              成员分组
            </Menu.Item>
            <Menu.Item key="/stores">
              门店管理
            </Menu.Item>
          </SubMenu>
        </Menu>
      </Sider>
      <Layout className="site-layout">
        <Header className="site-layout-background" style={{ padding: 0, display: 'flex', justifyContent: 'flex-end', paddingRight: '24px', background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}>
          <div className="user-dropdown">
            <Dropdown overlay={userMenu} trigger={['click']}>
              <span style={{ cursor: 'pointer' }}>
                <Avatar icon={<UserOutlined />} src={processAvatarUrl(user?.avatar)} />
                <span style={{ marginLeft: 8 }}>{user?.displayName || user?.username || '用户'}</span>
              </span>
            </Dropdown>
          </div>
        </Header>
        <Content style={{ margin: '16px'}}>
          <div className="site-layout-content">
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Layout>
    </SiderContext.Provider>
  );
};

export default AppLayout; 
